import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import "./Dashboard.css";
import type { User as BackendUser } from "../../types/user";
import Topbar from "Components/Topbar/Topbar";
import { usePageTitle } from "hooks/utility/usePageTitle";
import { useGetSaisies } from "hooks/saisie/useGetSaisies";
import { useGetSites } from "hooks/site/useGetSites";
import { useGetUsers } from "hooks/user/useGetUsers";
import type { Saisie } from "types/saisie";
import { BarChart3, CheckCircle2, Clock, XCircle, Search, Calendar, Inbox } from "lucide-react";

type DashboardProps = { user: BackendUser };

const MONTHS = [
  "Janvier",
  "Février",
  "Mars",
  "Avril",
  "Mai",
  "Juin",
  "Juillet",
  "Août",
  "Septembre",
  "Octobre",
  "Novembre",
  "Décembre",
];

const MONTH_ABBR = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Jun",
  "Jul",
  "Aoû",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

const Dashboard = ({ user }: DashboardProps) => {
  const navigate = useNavigate();
  const handleLogout = () => navigate("/");
  const pageTitle = usePageTitle();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedSite, setSelectedSite] = useState<number | null>(null);
  const [hoveredSaisie, setHoveredSaisie] = useState<Saisie | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const { data: sites = [], isLoading: sitesLoading } = useGetSites();
  const { data: users = [], isLoading: usersLoading } = useGetUsers();
  const { data: allSaisies = [], isLoading: saisiesLoading } = useGetSaisies({
    annee: selectedYear,
    siteId: selectedSite || undefined,
  });

  console.log("Dashboard - Selected Year:", selectedYear);
  console.log("Dashboard - Fetched Saisies:", allSaisies.length);
  console.log(
    "Dashboard - Saisies years:",
    allSaisies.map((s) => s.annee)
  );

  // Client-side filter to ensure we only show saisies for the selected year
  const filteredSaisiesByYear = useMemo(() => {
    return allSaisies.filter((saisie) => saisie.annee === selectedYear);
  }, [allSaisies, selectedYear]);

  const getUserDisplayName = (userId: number | null) => {
    if (!userId) return "Non assigné";
    const user = users.find((u) => u.id === userId);
    if (!user) return `User #${userId}`;
    return user.username || `User #${userId}`;
  };

  const getStatusConfig = (statut: string) => {
    if (statut === "valide") {
      return {
        color: "#10b981",
        label: "Validé",
        icon: "✓",
        bgColor: "#d1fae5",
      };
    }
    if (statut === "refuse" || statut === "rejete") {
      return {
        color: "#ef4444",
        label: "Rejeté",
        icon: "✗",
        bgColor: "#fee2e2",
      };
    }
    return {
      color: "#f59e0b",
      label: "En attente",
      icon: "⏳",
      bgColor: "#fef3c7",
    };
  };

  const processedData = useMemo(() => {
    const calendarData: Record<
      number,
      Record<number, { saisies: Saisie[]; counts: Record<string, number> }>
    > = {};

    let totalValidated = 0;
    let totalRejected = 0;
    let totalPending = 0;

    filteredSaisiesByYear.forEach((saisie) => {
      const siteId = saisie.site;
      const month = saisie.mois;

      if (!calendarData[siteId]) {
        calendarData[siteId] = {};
      }
      if (!calendarData[siteId][month]) {
        calendarData[siteId][month] = {
          saisies: [],
          counts: { validated: 0, rejected: 0, pending: 0 },
        };
      }

      calendarData[siteId][month].saisies.push(saisie);

      if (saisie.statut === "valide") {
        calendarData[siteId][month].counts.validated++;
        totalValidated++;
      } else if (saisie.statut === "refuse" || saisie.statut === "rejete") {
        calendarData[siteId][month].counts.rejected++;
        totalRejected++;
      } else {
        calendarData[siteId][month].counts.pending++;
        totalPending++;
      }
    });

    const totalSaisies = totalValidated + totalRejected + totalPending;

    return {
      calendarData,
      totalValidated,
      totalRejected,
      totalPending,
      totalSaisies,
      validationRate: totalSaisies ? Math.round((totalValidated / totalSaisies) * 100) : 0,
    };
  }, [filteredSaisiesByYear]);
  const filteredSites = useMemo(() => {
    if (selectedSite) {
      return sites.filter((s) => s.id === selectedSite);
    }
    // Only show sites that have saisies in the calendar data
    return sites.filter((s) => processedData.calendarData[s.id]);
  }, [sites, selectedSite, processedData.calendarData]);

  const isLoading = sitesLoading || saisiesLoading || usersLoading;

  const topbarProps = {
    title: pageTitle,
    userName: user.username,
    onLogout: handleLogout,
    user: user,
    sites: sites,
  };

  const handleCellHover = (saisies: Saisie[], event: React.MouseEvent<HTMLDivElement>) => {
    if (saisies.length > 0) {
      setHoveredSaisie(saisies[0]);
      setMousePosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleCellLeave = () => {
    setHoveredSaisie(null);
  };

  const handleResetFilters = () => {
    setSelectedSite(null);
    setSelectedYear(currentYear);
  };

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    const thisYear = new Date().getFullYear();
    // Add current year and previous 5 years, and next year
    for (let i = thisYear - 5; i <= thisYear + 1; i++) {
      years.add(i);
    }
    return Array.from(years).sort((a, b) => b - a);
  }, []);

  return (
    <div className="dashboard-content">
      <Topbar {...topbarProps} />

      <main className="main-dashboard">
        {/* Header Stats Section */}
        <div className="dashboard-header">
          <div className="header-stats">
            <div className="stat-card stat-card-total">
              <div className="stat-icon">
                <BarChart3 size={28} strokeWidth={2.5} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{processedData.totalSaisies}</div>
                <div className="stat-label">Saisies Totales</div>
              </div>
            </div>

            <div className="stat-card stat-card-validated">
              <div className="stat-icon">
                <CheckCircle2 size={28} strokeWidth={2.5} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{processedData.totalValidated}</div>
                <div className="stat-label">Validées</div>
                <div className="stat-percentage">{processedData.validationRate}%</div>
              </div>
            </div>

            <div className="stat-card stat-card-pending">
              <div className="stat-icon">
                <Clock size={28} strokeWidth={2.5} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{processedData.totalPending}</div>
                <div className="stat-label">En Attente</div>
              </div>
            </div>

            <div className="stat-card stat-card-rejected">
              <div className="stat-icon">
                <XCircle size={28} strokeWidth={2.5} />
              </div>
              <div className="stat-info">
                <div className="stat-value">{processedData.totalRejected}</div>
                <div className="stat-label">Rejetées</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-card">
          <div className="filters-header">
            <h3 className="filters-title">
              <Search size={18} strokeWidth={2.5} /> Filtres
            </h3>
            <button className="btn-reset" onClick={handleResetFilters}>
              Réinitialiser
            </button>
          </div>

          <div className="filters-grid">
            <div className="filter-group">
              <label className="filter-label">Année</label>
              <select
                className="filter-select"
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Site</label>
              <select
                className="filter-select"
                value={selectedSite || ""}
                onChange={(e) => setSelectedSite(e.target.value ? Number(e.target.value) : null)}
                disabled={sitesLoading}
              >
                <option value="">Tous les sites</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-tags">
            {selectedSite && (
              <span className="filter-tag">
                {sites.find((s) => s.id === selectedSite)?.name}
                <button className="filter-tag-remove" onClick={() => setSelectedSite(null)}>
                  ×
                </button>
              </span>
            )}
          </div>
        </div>

        {/* Calendar Section */}
        <div className="calendar-card">
          <div className="calendar-header">
            <h2 className="calendar-title">
              <Calendar size={22} strokeWidth={2.5} color="#10b981" /> Calendrier des Saisies -{" "}
              {selectedYear}
            </h2>
            <div className="calendar-legend">
              <div className="legend-item">
                <span className="legend-dot" style={{ background: "#10b981" }} />
                <span className="legend-label">Validé</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ background: "#f59e0b" }} />
                <span className="legend-label">En attente</span>
              </div>
              <div className="legend-item">
                <span className="legend-dot" style={{ background: "#ef4444" }} />
                <span className="legend-label">Rejeté</span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement des données...</p>
            </div>
          ) : filteredSites.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Inbox size={64} strokeWidth={1.5} />
              </div>
              <h3>Aucune donnée disponible</h3>
              <p>
                Aucune saisie trouvée pour les filtres sélectionnés.
                <br />
                Essayez de modifier vos critères de recherche.
              </p>
            </div>
          ) : (
            <div className="calendar-container">
              <div className="calendar-table-wrapper">
                <table className="calendar-table">
                  <thead>
                    <tr>
                      <th className="calendar-header-site">Site</th>
                      {MONTH_ABBR.map((month, idx) => (
                        <th key={idx} className="calendar-header-month">
                          {month}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSites.map((site) => (
                      <tr key={site.id} className="calendar-row">
                        <td className="calendar-cell-site">
                          <div className="site-name">{site.name}</div>
                        </td>
                        {Array.from({ length: 12 }).map((_, monthIdx) => {
                          const month = monthIdx + 1;
                          const cellData = processedData.calendarData[site.id]?.[month];
                          const saisies = cellData?.saisies || [];
                          const counts = cellData?.counts || {
                            validated: 0,
                            rejected: 0,
                            pending: 0,
                          };

                          const hasData = saisies.length > 0;

                          return (
                            <td
                              key={monthIdx}
                              className={`calendar-cell ${hasData ? "has-data" : ""}`}
                            >
                              {hasData ? (
                                <div
                                  className="cell-content"
                                  onMouseEnter={(e) => handleCellHover(saisies, e)}
                                  onMouseMove={(e) =>
                                    setMousePosition({
                                      x: e.clientX,
                                      y: e.clientY,
                                    })
                                  }
                                  onMouseLeave={handleCellLeave}
                                >
                                  <div className="status-indicators">
                                    {counts.validated > 0 && (
                                      <div
                                        className="status-badge validated"
                                        title={`${counts.validated} validée(s)`}
                                      >
                                        <CheckCircle2 size={24} strokeWidth={2.5} />
                                      </div>
                                    )}
                                    {counts.pending > 0 && (
                                      <div
                                        className="status-badge pending"
                                        title={`${counts.pending} en attente`}
                                      >
                                        <Clock size={24} strokeWidth={2.5} />
                                      </div>
                                    )}
                                    {counts.rejected > 0 && (
                                      <div
                                        className="status-badge rejected"
                                        title={`${counts.rejected} rejetée(s)`}
                                      >
                                        <XCircle size={24} strokeWidth={2.5} />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="cell-empty">-</div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Tooltip */}
        {hoveredSaisie && (
          <div
            className="saisie-tooltip"
            style={{
              left: `${mousePosition.x + 15}px`,
              top: `${mousePosition.y + 15}px`,
            }}
          >
            <div className="tooltip-header">
              <span
                className="tooltip-status-indicator"
                style={{
                  background: getStatusConfig(hoveredSaisie.statut).color,
                }}
              />
              <strong>{getStatusConfig(hoveredSaisie.statut).label}</strong>
            </div>
            <div className="tooltip-content">
              <div className="tooltip-row">
                <span className="tooltip-label">Site:</span>
                <span className="tooltip-value">
                  {sites.find((s) => s.id === hoveredSaisie.site)?.name ||
                    `Site ${hoveredSaisie.site}`}
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Période:</span>
                <span className="tooltip-value">
                  {MONTHS[hoveredSaisie.mois - 1]} {hoveredSaisie.annee}
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Date création:</span>
                <span className="tooltip-value">
                  {new Date(hoveredSaisie.date_creation).toLocaleDateString("fr-FR")}
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Indicateurs:</span>
                <span className="tooltip-value">
                  {Object.values(hoveredSaisie.postes_data || {}).reduce(
                    (total, valeurs) => total + valeurs.length,
                    0
                  )}
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">Créé par:</span>
                <span className="tooltip-value">
                  {getUserDisplayName(hoveredSaisie.created_by)}
                </span>
              </div>
              {hoveredSaisie.require_double_validation ? (
                <>
                  <div className="tooltip-row">
                    <span className="tooltip-label">1ère validation:</span>
                    <span className="tooltip-value">
                      {hoveredSaisie.first_validation_by
                        ? getUserDisplayName(hoveredSaisie.first_validation_by)
                        : "En attente"}
                    </span>
                  </div>
                  <div className="tooltip-row">
                    <span className="tooltip-label">Validation finale:</span>
                    <span className="tooltip-value">
                      {hoveredSaisie.final_validation_by
                        ? getUserDisplayName(hoveredSaisie.final_validation_by)
                        : "En attente"}
                    </span>
                  </div>
                </>
              ) : (
                <div className="tooltip-row">
                  <span className="tooltip-label">Validation:</span>
                  <span className="tooltip-value">
                    {hoveredSaisie.final_validation_by
                      ? getUserDisplayName(hoveredSaisie.final_validation_by)
                      : "En attente"}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
